import { useEffect } from 'react';
import { useMantineColorScheme } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { runRetroIntro } from './retro-intro.js';

/**
 * The third theme. Per viewer, stored locally like the colour scheme, and
 * applied as `data-retro` on <html> so `retro.css` can override the tokens.
 * It rides on top of Mantine's dark scheme so its components come out dark too.
 *
 * Entering plays a loud intro, so the key code below is the *only* way in —
 * see `exitRetro` for why nothing else can start it.
 */
const KEY = 'rm:retro';

export function isRetro(): boolean {
  try {
    return localStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Paints the stored theme. Used at boot to restore a choice the viewer already
 * made, so there is no flash of the default look — it never plays the intro.
 */
export function applyRetro(on: boolean): void {
  try {
    if (on) localStorage.setItem(KEY, '1');
    else localStorage.removeItem(KEY);
  } catch {
    // Private mode: the theme just will not survive a reload.
  }
  if (on) document.documentElement.dataset.retro = '';
  else delete document.documentElement.dataset.retro;
}

function setTheme(on: boolean, colorScheme: 'light' | 'dark'): void {
  applyRetro(on);
  // Set the attribute directly so the new snapshot already has it; Mantine's
  // own state catches up on the next render.
  document.documentElement.setAttribute('data-mantine-color-scheme', colorScheme);
}

/**
 * Leaves retro and lands on `colorScheme`. Silent: no intro, no audio.
 *
 * This is the only retro switch exported to the rest of the app, and it can
 * only ever turn the theme *off*. Entering is deliberately not exported — the
 * intro seizes the screen and plays music, so a stray click on a theme button
 * must never be able to start it. The key code below is the one way in.
 */
export function exitRetro(colorScheme: 'light' | 'dark'): void {
  if (!isRetro()) return;
  setTheme(false, colorScheme);
}

/** Enters retro behind the "Back in Black" intro. Module-local on purpose. */
function enterRetro(): void {
  void runRetroIntro(() => setTheme(true, 'dark'));
}

// ↑ ↑ ↓ ↓ ← →
const SEQUENCE = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];

/** Listens for the arrow-key code anywhere outside a text field and toggles retro. */
export function useRetroKeys(): void {
  const { setColorScheme } = useMantineColorScheme();

  useEffect(() => {
    let progress = 0;

    const onKey = (event: KeyboardEvent) => {
      // Real key presses only. A synthetic `keydown` dispatched by any other
      // code cannot reach the intro.
      if (!event.isTrusted) return;

      const target = event.target as HTMLElement | null;
      if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))) {
        return;
      }

      progress = event.key === SEQUENCE[progress] ? progress + 1 : event.key === SEQUENCE[0] ? 1 : 0;
      if (progress < SEQUENCE.length) return;
      progress = 0;

      const on = !isRetro();
      if (on) {
        enterRetro();
        setColorScheme('dark');
      } else {
        setTheme(false, 'dark');
      }
      notifications.show({
        color: on ? 'orange' : 'gray',
        title: on ? 'RETRO MODE ON' : 'Retro mode off',
        message: on ? 'Press the code again to go back.' : 'Back to the usual look.',
        autoClose: 3000,
      });
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setColorScheme]);
}
